import { Environment } from '@cobliteam/react-environments';
import { SessionManager } from '../account/session/SessionManager';
import { CobliAxios } from './CobliAxios';
import type HttpStatusCode from './HttpStatusCode';
import type { Configuration, IRequest } from './Types';

const env = Environment.getInstance().getEnvironment();

class Requester {
  private axiosInstance: CobliAxios;

  private abortController: AbortController | null = null;

  private sessionManager: SessionManager;

  constructor(
    configuration: Configuration,
    sessionManager: SessionManager = new SessionManager({
      host: env.herbieApiHost,
    })
  ) {
    this.axiosInstance = new CobliAxios(configuration);
    this.sessionManager = sessionManager;
  }

  public getAbortController = () => this.abortController;

  public request = (requestData: IRequest, nonReportableToDatadogErrors?: HttpStatusCode[]) => {
    // NOTE: as this controller is being shared on the same instance, using the same instance for
    // multiple requests will only allow to cancel the last one.
    this.abortController = new AbortController();

    return this._request(requestData, this.abortController.signal, nonReportableToDatadogErrors);
  };

  public externalRequest = (requestData: IRequest, nonReportableToDatadogErrors?: HttpStatusCode[]) => {
    // NOTE: as this controller is being shared on the same instance, using the same instance for
    // multiple requests will only allow to cancel the last one.
    this.abortController = new AbortController();
    return this._request(requestData, this.abortController.signal, nonReportableToDatadogErrors, true);
  };

  public requestWithSignal = (requestData: IRequest, signal: AbortSignal) => {
    return this._request(requestData, signal);
  };

  private _request = (requestData: IRequest, signal?: AbortSignal, nonReportableToDatadogErrors: HttpStatusCode[] = [], external = false) => {
    return this.sessionManager.maybeRenewAccessToken().then(() => this.axiosInstance.request(requestData, signal, nonReportableToDatadogErrors, external));
  };

  public getValidToken = (): Promise<string> => this.sessionManager.maybeRenewAccessToken();

  // Forces a session refresh regardless of the local expiry heuristic. Use to recover from a 401
  // when the locally-valid token was rejected server-side (e.g. client clock skew).
  public forceRefreshToken = (): Promise<string> => this.sessionManager.renewSession();
}

export default Requester;
